import React, { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { userService } from '../../services/userService';
import { authService } from '../../services/authService';

const ProfilePage: React.FC = () => {
  const { user } = useAuth();
  const [form, setForm] = useState({ firstName: '', lastName: '', username: '', email: '' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const [pw, setPw] = useState({ currentPassword: '', newPassword: '' });
  const [pwMsg, setPwMsg] = useState<string | null>(null);
  const [pwErr, setPwErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      if (!user) return;
      // Use the current user data from auth context instead of fetching from /api/users/:id
      // which requires ADMIN role. The user data is already available in the auth context.
      setForm({ firstName: user.firstName, lastName: user.lastName, username: user.username, email: user.email });
    })();
  }, [user]);

  const onSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setSaving(true); setMsg(null); setErr(null);
    try {
      await userService.update(user.id, form);
      setMsg('Profile updated');
    } catch (e: any) {
      setErr(e?.response?.data?.error || e?.response?.data?.message || 'Failed to update profile');
    } finally { setSaving(false); }
  };

  const onChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwMsg(null); setPwErr(null);
    try {
      await authService.changePassword({ currentPassword: pw.currentPassword, newPassword: pw.newPassword });
      setPwMsg('Password changed successfully');
      setPw({ currentPassword: '', newPassword: '' });
    } catch (e: any) {
      setPwErr(e?.response?.data?.message || 'Failed to change password');
    }
  };

  return (
    <div className="space-y-8">
      <div className="bg-white rounded-lg shadow-soft p-6">
        <h1 className="text-2xl font-bold mb-4">Profile</h1>
        {msg && <div className="mb-3 text-green-600 text-sm">{msg}</div>}
        {err && <div className="mb-3 text-red-600 text-sm">{err}</div>}
        <form onSubmit={onSave} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="form-label">First Name</label>
            <input className="form-input" value={form.firstName} onChange={e => setForm({ ...form, firstName: e.target.value })} />
          </div>
          <div>
            <label className="form-label">Last Name</label>
            <input className="form-input" value={form.lastName} onChange={e => setForm({ ...form, lastName: e.target.value })} />
          </div>
          <div>
            <label className="form-label">Username</label>
            <input className="form-input" value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} />
          </div>
          <div>
            <label className="form-label">Email</label>
            <input type="email" className="form-input" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
          </div>
          <div className="md:col-span-2">
            <button disabled={saving} className="btn-primary">{saving ? 'Saving...' : 'Save changes'}</button>
          </div>
        </form>
      </div>

      <div className="bg-white rounded-lg shadow-soft p-6">
        <h2 className="text-xl font-semibold mb-4">Change Password</h2>
        {pwMsg && <div className="mb-3 text-green-600 text-sm">{pwMsg}</div>}
        {pwErr && <div className="mb-3 text-red-600 text-sm">{pwErr}</div>}
        <form onSubmit={onChangePassword} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="form-label">Current Password</label>
            <input type="password" className="form-input" value={pw.currentPassword} onChange={e => setPw({ ...pw, currentPassword: e.target.value })} />
          </div>
          <div>
            <label className="form-label">New Password</label>
            <input type="password" className="form-input" value={pw.newPassword} onChange={e => setPw({ ...pw, newPassword: e.target.value })} />
          </div>
          <div className="md:col-span-2"><button className="btn-secondary">Change Password</button></div>
        </form>
      </div>
    </div>
  );
};

export default ProfilePage;

